import { mainApiService, apiUtils } from './apiService';
import { endpoints } from '../config/endpoints';
import { config } from '../config/environment';
import { AppointmentType } from '../constants/appointmentTypes';

// Registration Types
export interface ProviderRegistrationData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone: string;
  street_address?: string;
  city: string;
  state_province: string;
  postal_code?: string;
  country: string;
  currency?: string;
  website?: string;
  profile_description?: string;
  // Availability
  timezone?: string;
  working_hours_start?: string;
  working_hours_end?: string;
  allow_weekends?: boolean;
  // Frontend-only fields
  specialization: number[];
  yearsOfExperience: string;
  confirmPassword: string;
  agreeToTerms: boolean;
  email_pre_verified?: boolean;
  // Credentials & branding
  slug?: string;
  certification_ids?: number[];
  credential_number?: string;
  credential_issue_date?: string;
  institute_id?: number | null;
}

export interface ClientRegistrationData {
  coach_id?: string; // Only for coach-initiated registration
  first_name: string;
  last_name: string;
  email: string;
  password?: string; // Only for regular client registration
  phone: string;
  date_of_birth: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  street_address: string;
  city: string;
  state_province: string;
  postal_code: string;
  country: string;
  bio?: string; // Optional for coach-initiated
  confirmPassword?: string; // Only for regular client registration
  agreeToTerms?: boolean; // Only for regular client registration
}

export interface RegistrationResponse {
  success: boolean;
  message: string;
  userId?: string;
  userType?: 'provider' | 'client';
  requiresVerification?: boolean;
  verificationEmailSent?: boolean;
  accessToken?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
  userType: 'provider' | 'client';
}

export interface LoginResponse {
  success: boolean;
  message: string;
  token?: string;
  refreshToken?: string;
  mustResetPassword?: boolean;
  mustVerify?: boolean;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    userType: 'provider' | 'client';
  };
}

// Registration Service Class
class RegistrationService {
  // Fetch active specializations from the public API endpoint
  async getSpecializations(): Promise<{ id: number; name: string; description?: string }[]> {
    try {
      const response = await mainApiService.get<{ id: number; name: string; description?: string }[]>(
        endpoints.main.specializations
      );
      if (response.success && response.data) {
        return response.data;
      }
      return [];
    } catch {
      return [];
    }
  }

  // Provider Registration
  async registerProvider(data: ProviderRegistrationData): Promise<RegistrationResponse> {
    try {
      // Validate data before sending
      const validation = this.validateProviderData(data);
      if (!validation.isValid) {
        return {
          success: false,
          message: validation.errors.join(', '),
        };
      }

      // Map frontend data to API format
      const apiData = {
        email: data.email,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
        phone: data.phone,
        street_address: data.street_address || undefined,
        city: data.city,
        state_province: data.state_province,
        postal_code: data.postal_code || undefined,
        country: data.country,
        website: data.website || undefined,
        profile_description: data.profile_description || undefined,
        specialization_ids: data.specialization,
        timezone: data.timezone || 'UTC',
        working_hours_start: data.working_hours_start || '09:00',
        working_hours_end: data.working_hours_end || '17:00',
        allow_weekends: data.allow_weekends ?? false,
        email_pre_verified: data.email_pre_verified ?? false,
      };

      // Debug logging
      console.log('Raw form data received:', data);
      console.log('Mapped API data:', apiData);
      console.log('Endpoint:', endpoints.main.coaches.register);

      const response = await mainApiService.post<any>(
        endpoints.main.coaches.register,
        apiData
      );

      if (response.success && response.data) {
        return {
          success: true,
          message: response.data.message || 'Registration successful!',
          userId: response.data.coach?.id?.toString(),
          userType: 'provider',
          accessToken: response.data.access_token,
        };
      } else {
        return {
          success: false,
          message: response.error || 'Registration failed. Please try again.',
        };
      }
    } catch (error) {
      console.log('Registration service caught error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      return {
        success: false,
        message: errorMessage,
      };
    }
  }

  // Client Registration
  async registerClient(data: ClientRegistrationData, isCoachInitiated: boolean = false): Promise<RegistrationResponse> {
    try {
      // Validate data before sending
      const validation = this.validateClientData(data, isCoachInitiated);
      if (!validation.isValid) {
        return {
          success: false,
          message: validation.errors.join(', '),
        };
      }

      // Map frontend data to API payload format based on registration type
      const apiData = isCoachInitiated 
        ? {
            // Coach-initiated registration payload (no password or bio)
            coach_id: data.coach_id,
            first_name: data.first_name,
            last_name: data.last_name,
            email: data.email,
            phone: data.phone,
            date_of_birth: data.date_of_birth,
            emergency_contact_name: data.emergency_contact_name,
            emergency_contact_phone: data.emergency_contact_phone,
            street_address: data.street_address,
            city: data.city,
            state_province: data.state_province,
            postal_code: data.postal_code,
            country: data.country,
          }
        : {
            // Regular client registration payload (includes password, bio)
            first_name: data.first_name,
            last_name: data.last_name,
            email: data.email,
            password: data.password,
            phone: data.phone,
            date_of_birth: data.date_of_birth,
            emergency_contact_name: data.emergency_contact_name,
            emergency_contact_phone: data.emergency_contact_phone,
            street_address: data.street_address,
            city: data.city,
            state_province: data.state_province,
            postal_code: data.postal_code,
            country: data.country,
            bio: data.bio,
          };

      // Choose endpoint based on registration type
      const endpoint = isCoachInitiated 
        ? '/coaches/register-client' 
        : endpoints.main.clients.register;

      console.log('=== CLIENT REGISTRATION DEBUG ===');
      console.log('Is Coach Initiated:', isCoachInitiated);
      console.log('Endpoint:', endpoint);
      console.log('Full URL will be:', `${config.apiBaseUrl}${endpoint}`);
      console.log('Payload being sent:', JSON.stringify(apiData, null, 2));
      console.log('================================');

      const response = await mainApiService.post<any>(
        endpoint,
        apiData
      );

      if (response.success && response.data) {
        return {
          success: true,
          message: response.data.message || 'Registration successful!',
          userId: (response.data.client?.id || response.data.id)?.toString(),
          userType: 'client',
        };
      } else {
        return {
          success: false,
          message: response.error || 'Registration failed. Please try again.',
        };
      }
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Registration failed',
      };
    }
  }

  // Login
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await mainApiService.post<LoginResponse>(
        endpoints.main.auth.login,
        credentials
      );

      return apiUtils.handleResponse(response);
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Login failed',
      };
    }
  }

  // Coach Login
  async loginCoach(email: string, password: string): Promise<LoginResponse> {
    try {
      console.log('Coach login attempt:', { email, endpoint: endpoints.main.auth.loginCoach });
      
      const response = await mainApiService.post<any>(
        endpoints.main.auth.loginCoach,
        { email, password },
        undefined,
        true, // skipAuthRedirect — 401 means wrong password here, not expired session
      );

      console.log('Coach login response:', response);
      
      if (response.success && response.data) {
        // Server returns: {access_token, token_type, user_type, user_id, email, full_name, must_verify}
        const loginData = response.data;
        return {
          success: true,
          message: 'Login successful',
          token: loginData.access_token,
          mustVerify: loginData.must_verify ?? false,
          user: {
            id: loginData.user_id.toString(),
            email: loginData.email,
            firstName: loginData.full_name?.split(' ')[0] || '',
            lastName: loginData.full_name?.split(' ').slice(1).join(' ') || '',
            userType: loginData.user_type
          }
        };
      } else {
        return {
          success: false,
          message: response.error || 'Login failed',
        };
      }
    } catch (error) {
      console.error('Coach login error:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Login failed',
      };
    }
  }

  // Admin Login
  async loginAdmin(email: string, password: string): Promise<LoginResponse> {
    try {
      console.log('Admin login attempt:', { email, endpoint: endpoints.main.auth.loginAdmin });

      const response = await mainApiService.post<any>(
        endpoints.main.auth.loginAdmin,
        { email, password }
      );

      console.log('Admin login response:', response);

      if (response.success && response.data) {
        // Server returns: {access_token, token_type, user_type, user_id, email, full_name}
        const loginData = response.data;
        return {
          success: true,
          message: 'Login successful',
          token: loginData.access_token,
          user: {
            id: loginData.user_id.toString(),
            email: loginData.email,
            firstName: loginData.full_name?.split(' ')[0] || '',
            lastName: loginData.full_name?.split(' ').slice(1).join(' ') || '',
            userType: loginData.user_type,
          },
        };
      } else {
        return {
          success: false,
          message: response.error || 'Login failed',
        };
      }
    } catch (error) {
      console.error('Admin login error:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Login failed',
      };
    }
  }

  // Client Login
  async loginClient(email: string, password: string): Promise<LoginResponse> {
    try {
      console.log('Client login attempt:', { email, endpoint: endpoints.main.auth.loginClient });
      
      const response = await mainApiService.post<any>(
        endpoints.main.auth.loginClient,
        { email, password },
        undefined,
        true, // skipAuthRedirect — 401 means wrong password here, not expired session
      );

      console.log('Client login response:', response);
      
      if (response.success && response.data) {
        // Server returns: {access_token, token_type, user_type, user_id, email, full_name, must_reset_password, must_verify}
        const loginData = response.data;
        return {
          success: true,
          message: 'Login successful',
          token: loginData.access_token,
          mustResetPassword: loginData.must_reset_password ?? false,
          mustVerify: loginData.must_verify ?? false,
          user: {
            id: loginData.user_id.toString(),
            email: loginData.email,
            firstName: loginData.full_name?.split(' ')[0] || '',
            lastName: loginData.full_name?.split(' ').slice(1).join(' ') || '',
            userType: loginData.user_type
          }
        };
      } else {
        return {
          success: false,
          message: response.error || 'Login failed',
        };
      }
    } catch (error) {
      console.error('Client login error:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Login failed',
      };
    }
  }

  // Email Verification
  async verifyEmail(token: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await mainApiService.post<{ success: boolean; message: string }>(
        '/auth/verify-email',
        { token }
      );

      return apiUtils.handleResponse(response);
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Email verification failed',
      };
    }
  }

  // Resend Verification Email
  async resendVerificationEmail(email: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await mainApiService.post<{ success: boolean; message: string }>(
        '/auth/resend-verification',
        { email }
      );

      return apiUtils.handleResponse(response);
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to resend verification email',
      };
    }
  }

  // Password Reset
  async requestPasswordReset(email: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await mainApiService.post<{ success: boolean; message: string }>(
        '/auth/forgot-password',
        { email }
      );

      return apiUtils.handleResponse(response);
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Password reset request failed',
      };
    }
  }

  // Reset Password
  async resetPassword(
    token: string, 
    newPassword: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await mainApiService.post<{ success: boolean; message: string }>(
        '/auth/reset-password',
        { token, new_password: newPassword }
      );

      return apiUtils.handleResponse(response);
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Password reset failed',
      };
    }
  }

  // Validation Methods
  private validateProviderData(data: ProviderRegistrationData): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!data.first_name.trim()) errors.push('First name is required');
    if (!data.last_name.trim()) errors.push('Last name is required');
    if (!data.email.trim()) errors.push('Email is required');
    else if (!/\S+@\S+\.\S+/.test(data.email)) errors.push('Email is invalid');
    
    if (!data.phone.trim()) errors.push('Business phone is required');
    if (!data.city.trim()) errors.push('City is required');
    if (!data.state_province.trim()) errors.push('State/Province is required');
    if (!data.country.trim()) errors.push('Country is required');
    
    if (data.specialization.length === 0) errors.push('At least one specialization is required');
    if (!data.yearsOfExperience) errors.push('Years of experience is required');
    
    if (!data.password) errors.push('Password is required');
    else if (data.password.length < 12) errors.push('Password must be at least 12 characters');
    else if (!/[A-Z]/.test(data.password)) errors.push('Password must contain at least one uppercase letter');
    else if (!/[a-z]/.test(data.password)) errors.push('Password must contain at least one lowercase letter');
    else if (!/[0-9]/.test(data.password)) errors.push('Password must contain at least one digit');

    if (!data.confirmPassword) errors.push('Please confirm your password');
    else if (data.password !== data.confirmPassword) errors.push('Passwords do not match');
    
    // if (!data.agreeToTerms) errors.push('You must agree to the terms and conditions');

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  private validateClientData(data: ClientRegistrationData, isCoachInitiated: boolean = false): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Common validations for both registration types
    if (!data.first_name.trim()) errors.push('First name is required');
    if (!data.last_name.trim()) errors.push('Last name is required');
    if (!data.email.trim()) errors.push('Email is required');
    else if (!/\S+@\S+\.\S+/.test(data.email)) errors.push('Email is invalid');
    
    if (!data.phone.trim()) errors.push('Phone number is required');
    if (!data.date_of_birth) errors.push('Date of birth is required');
    if (!data.city.trim()) errors.push('City is required');
    if (!data.state_province.trim()) errors.push('State/Province is required');
    if (!data.postal_code.trim()) errors.push('Postal code is required');
    if (!data.country.trim()) errors.push('Country is required');
    
    // Coach-initiated specific validations
    if (isCoachInitiated) {
      if (!data.coach_id) errors.push('Coach ID is required');
    } else {
      // Regular client registration validations
      if (!data.password) errors.push('Password is required');
      else if (data.password.length < 12) errors.push('Password must be at least 12 characters');
      
      if (!data.confirmPassword) errors.push('Please confirm your password');
      else if (data.password !== data.confirmPassword) errors.push('Passwords do not match');
      
      // if (!data.agreeToTerms) errors.push('You must agree to the terms and conditions');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  // Sessions Service
  async getCoachSessions(coachId: string, upcomingOnly = false): Promise<{ success: boolean; data?: any[]; error?: string }> {
    try {
      const qs = upcomingOnly ? '' : '?upcoming_only=false';
      const response = await mainApiService.get<any[]>(
        endpoints.main.coaches.sessions(coachId) + qs
      );
      if (response.success && response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: response.error || 'Failed to fetch sessions' };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch sessions',
      };
    }
  }

  async createSession(sessionData: {
    coach_id: string;
    client_id: string;
    meeting_name: string;
    scheduled_at: string;
    duration_minutes: number;
    meeting_url: string;
    appointment_type?: AppointmentType;
    barter_note?: string;
    event_type_id?: string;
    package_enrollment_id?: string;
  }): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const response = await mainApiService.post<any>(
        endpoints.main.coaches.createSession,
        sessionData
      );
      if (response.success && response.data) {
        return { success: true, data: response.data };
      } else {
        return { success: false, error: response.error || 'Failed to create session' };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to create session',
      };
    }
  }

  // Utility Methods
  async checkEmailAvailability(email: string): Promise<{ available: boolean; message: string }> {
    try {
      const response = await mainApiService.get<{ available: boolean; message: string }>(
        `/auth/check-email?email=${encodeURIComponent(email)}`
      );

      return apiUtils.handleResponse(response);
    } catch (error) {
      return {
        available: false,
        message: error instanceof Error ? error.message : 'Email check failed',
      };
    }
  }

  async getRegistrationStats(): Promise<{
    totalProviders: number;
    totalClients: number;
    recentRegistrations: number;
  }> {
    try {
      const response = await mainApiService.get<{
        totalProviders: number;
        totalClients: number;
        recentRegistrations: number;
      }>('/auth/registration-stats');

      return apiUtils.handleResponse(response);
    } catch (error) {
      return {
        totalProviders: 0,
        totalClients: 0,
        recentRegistrations: 0,
      };
    }
  }
}

// Export singleton instance
export const registrationService = new RegistrationService();
export default registrationService;
